import { RolloutProgressReportEntities } from '../../../shared/self-inspection-reports/service/entities';
import {
  IconPaths,
  MapIconsStyles,
  ReportStatus,
} from '../../span-progress-entities';
import {
  landAgreementColors,
  pointInfo,
  reportStatusColors,
} from '../map-view-constants';

export interface IconData {
  color?: string;
  borderWidth?: number;
  size?: number;
  style?: 'solid' | 'dashed';
  isIcon?: boolean;
  iconStyle?: MapIconsStyles;
  iconPath?: IconPaths;
}

interface LegendItems {
  description: string;
  icon: IconData;
}

export interface LayerDescription {
  layerName: string;
  type: 'polygon' | 'line' | 'marker';
  legendItems: LegendItems[];
}

const spanLayer: LayerDescription = {
  type: 'line',
  layerName: 'legend.spanLayer',
  legendItems: [
    {
      icon: { color: reportStatusColors[ReportStatus.Done] },
      description: ReportStatus.Done,
    },
    {
      icon: { color: reportStatusColors[ReportStatus.Deviation] },
      description: ReportStatus.Deviation,
    },
    {
      icon: { color: reportStatusColors[ReportStatus.InProgress] },
      description: ReportStatus.InProgress,
    },
    {
      icon: { color: reportStatusColors[ReportStatus.Unknown] },
      description: ReportStatus.Unknown,
    },
  ],
};

const spanLayerV2: LayerDescription = {
  type: 'line',
  layerName: 'legend.spanLayer',
  legendItems: [
    {
      icon: {
        color:
          RolloutProgressReportEntities.v3.SpanColor[
            RolloutProgressReportEntities.v3.SpanStaus.SELECTED
          ],
      },
      description:
        'legend.spanV2.' + RolloutProgressReportEntities.v3.SpanStaus.SELECTED,
    },
    {
      icon: {
        color:
          RolloutProgressReportEntities.v3.SpanColor[
            RolloutProgressReportEntities.v3.SpanStaus.PLANNED
          ],
      },
      description:
        'legend.spanV2.' + RolloutProgressReportEntities.v3.SpanStaus.PLANNED,
    },
    {
      icon: {
        color:
          RolloutProgressReportEntities.v3.SpanColor[
            RolloutProgressReportEntities.v3.SpanStaus.IN_PROGRESS
          ],
      },
      description:
        'legend.spanV2.' +
        RolloutProgressReportEntities.v3.SpanStaus.IN_PROGRESS,
    },
    {
      icon: {
        color:
          RolloutProgressReportEntities.v3.SpanColor[
            RolloutProgressReportEntities.v3.SpanStaus.DONE
          ],
      },
      description:
        'legend.spanV2.' + RolloutProgressReportEntities.v3.SpanStaus.DONE,
    },
    {
      icon: {
        color:
          RolloutProgressReportEntities.v3.SpanColor[
            RolloutProgressReportEntities.v3.SpanStaus.RESTORED
          ],
      },
      description:
        'legend.spanV2.' + RolloutProgressReportEntities.v3.SpanStaus.RESTORED,
    },
    {
      icon: {
        color:
          RolloutProgressReportEntities.v3.SpanColor[
            RolloutProgressReportEntities.v3.SpanStaus.DEVIATED
          ],
      },
      description:
        'legend.spanV2.' + RolloutProgressReportEntities.v3.SpanStaus.DEVIATED,
    },
    {
      icon: {
        color: RolloutProgressReportEntities.v3.SpanColor.EXISTING,
      },
      description: 'legend.spanV2.EXISTING',
    },
  ],
};

const distpointLayer: LayerDescription = {
  type: 'marker',
  layerName: 'legend.distpointLayer',
  legendItems: [
    {
      icon: {
        color: pointInfo['HC'].pointStyle.fillColor,
        size: 18,
      },
      description: 'legend.hc',
    },
    {
      icon: {
        color: pointInfo['HP'].pointStyle.fillColor,
        size: 18,
      },
      description: 'legend.hp',
    },
    {
      icon: {
        color: pointInfo['INFO'].pointStyle.fillColor,
        borderWidth: pointInfo['INFO'].pointStyle.weight,
        size: 18,
      },
      description: 'legend.info',
    },
    {
      icon: {
        color: pointInfo['TRV'].pointStyle.fillColor,
        borderWidth: pointInfo['TRV'].pointStyle.weight,
        size: 18,
      },
      description: 'legend.trv',
    },
    {
      icon: {
        color: pointInfo['SDP'].pointStyle.fillColor,
        size: 18,
        isIcon: true,
        iconStyle: MapIconsStyles.RHOMBUS,
      },
      description: 'legend.sdp',
    },
    {
      icon: {
        color: pointInfo['KP'].pointStyle.fillColor,
        size: 18,
        isIcon: true,
      },
      description: 'legend.kp',
    },
    {
      icon: {
        color: pointInfo['WELL'].pointStyle.fillColor,
        borderWidth: pointInfo['WELL'].pointStyle.weight,
        size: 18,
      },
      description: 'legend.well',
    },
    {
      icon: {
        color: pointInfo['CABINET'].pointStyle.fillColor,
        size: 18,
        isIcon: true,
        iconStyle: MapIconsStyles.SQUARE,
      },
      description: 'legend.cabinet',
    },
    {
      icon: {
        color: pointInfo['SITE'].pointStyle.fillColor,
        size: 18,
        isIcon: true,
        iconStyle: MapIconsStyles.SHEILD,
      },
      description: 'legend.site',
    },
    {
      icon: {
        color: pointInfo['HUB'].pointStyle.fillColor,
        size: 18,
        isIcon: true,
        iconStyle: MapIconsStyles.RHOMBUS,
      },
      description: 'legend.hub',
    },
    {
      icon: {
        color: pointInfo['JOINTPOINT'].pointStyle.fillColor,
        borderWidth: pointInfo['JOINTPOINT'].pointStyle.weight,
        size: 18,
      },
      description: 'legend.jointpoint',
    },
    {
      icon: {
        isIcon: true,
        iconPath: IconPaths.MULTIHC_UNKNOWN,
      },
      description: 'legend.multiHc',
    },
    {
      icon: {
        isIcon: true,
        iconPath: IconPaths.MULTIHP_UNKNOWN,
      },
      description: 'legend.multiHp',
    },
    {
      icon: {
        isIcon: true,
        iconPath: IconPaths.MULTIHOUSEHOLD_UNKNOWN,
      },
      description: 'legend.multiHousehold',
    },
    {
      icon: {
        isIcon: true,
        iconPath: IconPaths.MULTIWELL_UNKNOWN,
      },
      description: 'legend.multiWell',
    },
  ],
};

const loaLayer: LayerDescription = {
  type: 'polygon',
  layerName: 'legend.loa',
  legendItems: [
    {
      icon: { color: landAgreementColors.DONE, size: 18 },
      description: 'legend.loaDone',
    },
    {
      icon: { color: landAgreementColors.NOT_COMPLETED, size: 18 },
      description: 'legend.loaRequired',
    },
  ],
};

export const legendData: LayerDescription[] = [
  spanLayer,
  distpointLayer,
  loaLayer,
];

export const legendDataV2: LayerDescription[] = [
  spanLayerV2,
  distpointLayer,
  loaLayer,
];
